import { useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react';

interface UsePortalDropdownParams {
  triggerClassName: string;
  groupClassName?: string;
  align?: 'left' | 'right';
}

interface UsePortalDropdownReturn {
  openId: string | null;
  pos: { top: number; left: number } | null;
  menuRef: React.RefObject<HTMLDivElement | null>;
  open: (id: string, triggerEl: HTMLElement) => void;
  close: () => void;
}

const adjustDropdownPosition = (
  menuEl: HTMLElement | null,
  anchorRect: DOMRect,
  align: 'left' | 'right',
): { top: number; left: number } => {
  const gap = 4;
  let top = anchorRect.bottom + gap;
  // align='right' のときはメニューの右端をアンカーの右端に揃える。
  // メニュー幅は実マウント後にしか確定しないので、初回は anchorRect.right を仮置きし
  // useLayoutEffect で正確に補正する。
  let left = align === 'right' ? anchorRect.right : anchorRect.left;
  if (menuEl) {
    const menuHeight = menuEl.offsetHeight;
    const menuWidth = menuEl.offsetWidth;
    if (top + menuHeight > window.innerHeight) {
      top = anchorRect.top - menuHeight - gap;
    }
    if (top < 0) {
      top = gap;
    }
    if (align === 'right') {
      left = anchorRect.right - menuWidth;
      if (left < gap) left = gap;
    }
  }
  return { top, left };
};

export type { UsePortalDropdownReturn };

export function usePortalDropdown({
  triggerClassName,
  groupClassName,
  align = 'left',
}: UsePortalDropdownParams): UsePortalDropdownReturn {
  const [openId, setOpenId] = useState<string | null>(null);
  const [pos, setPos] = useState<{ top: number; left: number } | null>(null);
  const menuRef = useRef<HTMLDivElement>(null);
  const anchorElRef = useRef<HTMLElement | null>(null);

  useEffect(() => {
    if (!openId) return;
    const handleClickOutside = (e: globalThis.MouseEvent) => {
      const target = e.target as HTMLElement;
      if (menuRef.current?.contains(target) || target.closest(`.${triggerClassName}`)) return;
      setOpenId(null);
    };
    // Escape はドロップダウンだけを閉じる。capture で受けて伝播を止め、同じキー押下で
    // 外側のダイアログ (最後のフレーム / 調整) まで閉じないようにする
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key !== 'Escape') return;
      e.stopPropagation();
      setOpenId(null);
    };
    document.addEventListener('mousedown', handleClickOutside);
    document.addEventListener('keydown', handleEscape, true);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      document.removeEventListener('keydown', handleEscape, true);
    };
  }, [openId, triggerClassName]);

  // 開いている間は anchor の位置・サイズ変化に追従する。
  useLayoutEffect(() => {
    if (!openId || !anchorElRef.current) return;
    const anchorEl = anchorElRef.current;
    const recompute = () => {
      const rect = anchorEl.getBoundingClientRect();
      setPos(adjustDropdownPosition(menuRef.current, rect, align));
    };
    recompute();
    // capture=true でネストされたスクロールコンテナのスクロールも検知
    window.addEventListener('scroll', recompute, true);
    window.addEventListener('resize', recompute);
    const ro = new ResizeObserver(recompute);
    ro.observe(anchorEl);
    return () => {
      window.removeEventListener('scroll', recompute, true);
      window.removeEventListener('resize', recompute);
      ro.disconnect();
    };
  }, [openId, align]);

  const open = useCallback(
    (id: string, triggerEl: HTMLElement) => {
      if (openId === id) {
        setOpenId(null);
        return;
      }
      const groupEl = groupClassName
        ? (triggerEl.closest(`.${groupClassName}`) as HTMLElement | null)
        : null;
      const anchorEl = groupEl ?? triggerEl;
      anchorElRef.current = anchorEl;
      setPos(adjustDropdownPosition(null, anchorEl.getBoundingClientRect(), align));
      setOpenId(id);
    },
    [openId, groupClassName, align],
  );

  const close = useCallback(() => {
    setOpenId(null);
  }, []);

  return { openId, pos, menuRef, open, close };
}
